import React, { useEffect, useState } from "react";
import { Container, Card, Table, Form, Badge, Spinner } from "react-bootstrap";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../axiosConfig";
import { toast } from "react-toastify";
import WooCompanyControl from "./WooCompanyControl";

export default function SuperadminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const platform=user?.is_superuser&&user?.user_name==='superadmin';
  const [wooSites,setWooSites]=useState({});
  const updateWoo=site=>setWooSites(prev=>({...prev,[site.company_id]:site}));
  useEffect(()=>{if(!platform)return;let alive=true;const load=()=>api.get('/superadmin/woo').then(r=>{if(alive)setWooSites(Object.fromEntries(r.data.map(s=>[s.company_id,s])));}).catch(()=>{});load();const timer=setInterval(load,10000);return()=>{alive=false;clearInterval(timer);};},[platform]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!user?.is_superuser) {
      toast.error("Acces interzis. Doar superadmin.");
      navigate("/");
      return;
    }
    fetchCompanies();
  }, [user, navigate]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get("/superadmin/companies");
      setCompanies(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Eroare la încărcarea companiilor");
    } finally {
      setLoading(false);
    }
  };

  const toggleAccess = async (companyId, currentStatus) => {
    try {
      const res = await api.put(`/superadmin/companies/${companyId}/toggle-access`);
      const newStatus = res.data.is_active;
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, is_active: newStatus } : c))
      );
      toast.success(`Acces ${newStatus ? 'Permis' : 'Blocat'} cu succes.`);
    } catch (err) {
      console.error(err);
      toast.error("Eroare la modificarea accesului.");
    }
  };

  const updateMaxUsers = async (companyId, newMax) => {
    try {
      const res = await api.put(`/superadmin/companies/${companyId}/max-users`, { max_users: newMax });
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, max_users: res.data.max_users } : c))
      );
      toast.success("Limita utilizatori actualizată.");
    } catch (err) {
      console.error(err);
      toast.error("Eroare la setarea limitei.");
    }
  };

  const updateAppVersion = async (companyId, newVersion) => {
    try {
      const res = await api.put(`/superadmin/companies/${companyId}/app-version`, { app_version: newVersion });
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, app_version: res.data.app_version } : c))
      );
      toast.success("Versiune aplicație actualizată.");
    } catch (err) {
      console.error(err);
      toast.error("Eroare la setarea versiunii.");
    }
  };

  const updateDocClosing = async (companyId, newValue) => {
    try {
      const res = await api.put(`/superadmin/companies/${companyId}/doc-closing`, { use_doc_closing: newValue });
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, use_doc_closing: res.data.use_doc_closing } : c))
      );
      toast.success("Opțiune închidere document actualizată.");
      window.dispatchEvent(new Event("document-settings-changed"));
      localStorage.setItem("document-settings-changed", String(Date.now()));
    } catch (err) {
      console.error(err);
      toast.error("Eroare la setarea opțiunii.");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    return d.toLocaleString("ro-RO");
  };

  const filteredCompanies = companies.filter(c => 
    (c.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.code || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card className="shadow-sm border-danger">
        <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">🛡️ Panou Superadmin - Management Firme</h4>
          <Badge bg="light" text="danger">Total: {companies.length}</Badge>
        </Card.Header>
        <Card.Body>
          <div className="mb-3 w-50">
            <Form.Control
              type="text"
              placeholder="Caută firmă (după nume sau cod)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Table responsive hover bordered className="align-middle text-center">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Cod Bază Date</th>
                <th>Nume Firmă</th>
                <th>Utilizatori Curenti</th>
                <th>Limita Max Useri</th>
                <th>Versiune Aplicație</th>
                <th>Finalizare documente</th>
                <th>Ultima Conectare</th>
                <th>Stare Acces</th>
                <th>Blocare / Deblocare</th>
                {platform&&<th style={{minWidth:390,width:390,position:"sticky",right:0,zIndex:3,background:"#f8f9fa",boxShadow:"-3px 0 6px rgba(0,0,0,.12)"}}>WooCommerce</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td className="fw-bold text-primary">{c.code}</td>
                  <td>{c.name}</td>
                  <td>
                    <Badge bg="secondary">{c.users_count}</Badge>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <Form.Control
                        type="number"
                        min="0"
                        style={{ width: "80px", textAlign: "center" }}
                        value={c.max_users || 0}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setCompanies((prev) =>
                            prev.map((comp) => (comp.id === c.id ? { ...comp, max_users: val } : comp))
                          );
                        }}
                        onBlur={(e) => updateMaxUsers(c.id, parseInt(e.target.value) || 0)}
                      />
                      <small className="text-muted" style={{ fontSize: "0.75rem", lineHeight: 1, textAlign: "left" }}>0 = Nelimitat</small>
                    </div>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={c.app_version || "STANDARD"}
                      onChange={(e) => updateAppVersion(c.id, e.target.value)}
                      style={{ minWidth: "120px" }}
                    >
                      <option value="LITE">LITE</option>
                      <option value="STANDARD">STANDARD</option>
                      <option value="SANTIER">SANTIER</option>
                      <option value="SERVICE_AUTO">SERVICE_AUTO</option>
                      <option value="CONTOR">CONTOR</option>
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      id={`switch-dc-${c.id}`}
                      aria-label={`Finalizare documente pentru ${c.name}`}
                      title="Afișează Finalizează și Redeschide în Intrare, Ieșire și NIR. Documentele deja finalizate rămân protejate și când opțiunea este inactivă."
                      checked={c.use_doc_closing !== false}
                      onChange={(e) => updateDocClosing(c.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    {c.last_login ? (
                      formatDate(c.last_login)
                    ) : (
                      <span className="text-muted fst-italic">Niciodată</span>
                    )}
                  </td>
                  <td>
                    {c.is_active ? (
                      <Badge bg="success">ACTIV</Badge>
                    ) : (
                      <Badge bg="danger">BLOCAT</Badge>
                    )}
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      id={`switch-${c.id}`}
                      checked={c.is_active}
                      onChange={() => toggleAccess(c.id, c.is_active)}
                    />
                  </td>
                  {platform&&<td style={{minWidth:390,width:390,whiteSpace:"normal",overflow:"visible",position:"sticky",right:0,zIndex:2,background:"white",boxShadow:"-3px 0 6px rgba(0,0,0,.12)"}}><WooCompanyControl company={c} site={wooSites[c.id]} onChange={updateWoo}/></td>}
                </tr>
              ))}
              {filteredCompanies.length === 0 && (
                <tr>
                  <td colSpan={platform?11:10} className="text-center text-muted py-4">
                    Nu s-au găsit firme.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}


